import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Youtube, MessageCircle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { getMl } from "@/lib/api";

const Footer = ({ siteSettings }) => {
  const { t, lang } = useI18n();
  const social = siteSettings?.social_links || {};
  const contact = siteSettings?.contact || {};
  const company = siteSettings?.company_info || {};

  const socialItems = [
    { key: "instagram", Icon: Instagram, label: "Instagram" },
    { key: "facebook", Icon: Facebook, label: "Facebook" },
    { key: "tiktok", Icon: () => (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1Z"/></svg>
      ), label: "TikTok" },
    { key: "linkedin", Icon: Linkedin, label: "LinkedIn" },
    { key: "youtube", Icon: Youtube, label: "YouTube" },
    { key: "whatsapp", Icon: MessageCircle, label: "WhatsApp" },
  ];

  return (
    <footer className="bg-[#0A1128] text-white relative overflow-hidden">
      <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
      {/* Top */}
      <div className="container-nakia py-20 grid grid-cols-1 md:grid-cols-12 gap-12 relative">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-10 h-10 border border-[#D4AF37] flex items-center justify-center rounded-sm">
              <span className="font-display text-[#D4AF37] text-xl">N</span>
            </div>
            <div>
              <div className="font-display text-2xl tracking-[0.3em]">NAKIA</div>
              <div className="text-[10px] text-[#D4AF37]/80 tracking-[0.35em] mt-1">GLOBAL SEJAHTERA</div>
            </div>
          </div>
          <p className="text-white/70 text-sm leading-relaxed max-w-sm">
            {getMl(company.tagline, lang) || t("footer.tagline")}
          </p>
          <div className="mt-7 flex items-center gap-3">
            {socialItems.map(({ key, Icon, label }) => {
              const url = social[key];
              if (!url) return null;
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${key}`}
                  aria-label={label}
                  className="w-10 h-10 flex items-center justify-center border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/70 transition-colors rounded-sm"
                >
                  <Icon size={16} />
                </a>
              );
            })}
          </div>
        </div>

        {/* Quick Links */}
        <div className="md:col-span-3">
          <h4 className="text-eyebrow text-[#D4AF37] mb-5">{t("footer.quickLinks")}</h4>
          <ul className="space-y-3 text-sm text-white/75">
            <li><Link data-testid="footer-link-about" to="/about" className="hover:text-[#D4AF37] transition-colors">{t("nav.about")}</Link></li>
            <li><Link data-testid="footer-link-export" to="/export-commodities" className="hover:text-[#D4AF37] transition-colors">{t("nav.export")}</Link></li>
            <li><Link data-testid="footer-link-import" to="/import-products" className="hover:text-[#D4AF37] transition-colors">{t("nav.import")}</Link></li>
            <li><Link data-testid="footer-link-services" to="/services" className="hover:text-[#D4AF37] transition-colors">{t("nav.services")}</Link></li>
            <li><Link data-testid="footer-link-catalogue" to="/catalogue" className="hover:text-[#D4AF37] transition-colors">{t("nav.catalogue")}</Link></li>
            <li><Link data-testid="footer-link-news" to="/news" className="hover:text-[#D4AF37] transition-colors">{t("nav.news")}</Link></li>
            <li><Link data-testid="footer-link-contact" to="/contact" className="hover:text-[#D4AF37] transition-colors">{t("nav.contact")}</Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-5">
          <h4 className="text-eyebrow text-[#D4AF37] mb-5">{t("footer.offices")}</h4>
          <ul className="space-y-4 text-sm text-white/80">
            {contact.address && (
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <span>{contact.address}</span>
              </li>
            )}
            {contact.email && (
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <a href={`mailto:${contact.email}`} className="hover:text-[#D4AF37] transition-colors">{contact.email}</a>
              </li>
            )}
            {contact.phone && (
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <a href={`tel:${contact.phone}`} className="hover:text-[#D4AF37] transition-colors">{contact.phone}</a>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-nakia py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/55">
          <p>© {new Date().getFullYear()} Nakia Global Sejahtera. {t("footer.rights")}</p>
          <p className="tracking-[0.25em] uppercase text-[10px]">Indonesia · Türkiye · Singapore</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
